import { useCallback } from 'react'
import { useStore } from 'jotai'
import { useArchiveStagedActions, type StagedAction } from '../../../hooks/use-staged-actions'
import { useReminderActions } from '../../../hooks/use-reminders'
import { useViewActions } from '../../../view-atoms'
import { trackActionEvent } from '../track-action-event'
import { actionInlineDraftsAtom } from '../inline-draft-state'
import { discardDraft } from '../inline-drafts'

/**
 * Archive/restore for the open action, shared by the control row's utility
 * cluster and the compact overflow menu — the same control in two chromes, so
 * the wording and the tracking live here rather than in whichever renders it.
 *
 * Archive, not delete: the action stays recoverable from History, and restoring
 * puts it back in the Outbox with the view still open.
 */
export function useActionArchiveControl(action: StagedAction) {
  const { dismissStagedAction, bulkUnarchiveStagedActions } = useArchiveStagedActions()
  const { closeView } = useViewActions()

  const isArchived = !!action.archivedAt

  const archive = useCallback(() => {
    dismissStagedAction(action.id).catch(console.error)
    closeView()
    trackActionEvent(action, 'archive')
  }, [action, dismissStagedAction, closeView])

  const restore = useCallback(() => {
    bulkUnarchiveStagedActions([action.id]).catch(console.error)
    trackActionEvent(action, 'restore')
  }, [action, bulkUnarchiveStagedActions])

  return { isArchived, label: isArchived ? 'Restore' : 'Archive', onSelect: isArchived ? restore : archive }
}

/** Accepting a conversation action: it becomes a todo in Reminders and leaves the actions list. */
export function useAddActionAsTodo(action: StagedAction) {
  const { addItem } = useReminderActions()
  const { dismissStagedAction } = useArchiveStagedActions()
  const { closeView } = useViewActions()
  const store = useStore()

  return useCallback(() => {
    // The field's unsaved title wins: the action is archived, so saving it there first would be wasted
    const draftTitle = store.get(actionInlineDraftsAtom)[action.id]?.title
    const text = [draftTitle, action.view?.title, action.title, action.view?.content]
      .map((candidate) => candidate?.trim())
      .find(Boolean)
    if (!text) return
    addItem(text, 'task')
    store.set(actionInlineDraftsAtom, (drafts) => discardDraft(drafts, action.id))
    dismissStagedAction(action.id).catch(console.error)
    closeView()
    trackActionEvent(action, 'add-todo')
  }, [action, addItem, dismissStagedAction, closeView, store])
}
